import React from 'react';
import { i18n } from 'i18n';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalDate } from '@js-joda/core';
import { Dayjs } from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { map, objOf } from 'ramda';
import { useQueryClient } from 'react-query';
import {
  UpdateModelNodeDataForm,
  UpdateStopeDataForm,
  SpoolStatus,
  SpoolUpdateForm,
  invalidateBulkNodeKeys,
  refreshMineModel,
  useBulkUpdateNodesMutation,
  useBulkUpdateNodesSilentMutation,
  useBulkUpdateSpoolsMutation,
  useBulkUpdateSpoolsSilentMutation,
  useUpdateStopeMutation,
  useUpdateStopeSilentMutation,
  useGetPourPresetGroupsQuery
} from 'providers/api';
import useRoles from 'hooks/useRoles';
import { isAdminUser, isPcUser } from 'utils/roles';
import { dayJsToLocalDate, LocalDateToDayJs } from 'utils';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { spoolEndsLabel } from '../../MineModel/Spools/utils';
import PCStope from '../../MineModel/Stopes/PCStope';
import { SPOOL_NAME_MAX_LENGTH, SPOOL_OTHER_MAX_LENGTH, SPOOL_TAG_MAX_LENGTH } from '../../SpoolForm/spoolFormUtils';
import { generatePipeTypeLegend, useSceneLegend } from '../../Components/SceneLegend';
import { getBulkEditSceneState, subscribeToBulkEditSceneState } from './bulkEditSceneState';
import { splitSelectedIdsByType } from './Toolbar/utils';

interface BulkEditPropertiesPanelProps {
  model: Model;
}

const MULTIPLE = '(multiple values)';

type SpoolTextField = 'name' | 'level' | 'area' | 'other';

const textPatch = <F extends SpoolTextField>(field: F, value: string | undefined): Partial<Record<F, string>> => objOf(field, value);

const buildNodeUpdateForm = (node: PCNode): UpdateModelNodeDataForm => ({
  nodeId: node.id,
  modelNodeDataForm: {
    nodeId: node.id,
    point: node.coords,
    pipeId: node.pipeId,
    pit: node.pit,
    isPit: node.isPit,
    isStope: node.isStope ?? false,
    level: node.level,
    area: node.area,
    isPlantNode: node.isPlantNode,
    boosterPressure: node.boosterPressure ?? 0,
    ruptureDiskPressure: node.ruptureDiskPressure ?? 0,
    chokeHead: node.chokeHead ?? 0,
  },
});

const buildStopeUpdateForm = (stope: PCStope, partial: Partial<UpdateStopeDataForm['stopeDataForm']>): UpdateStopeDataForm => ({
  nodeId: stope.originNode.id,
  stopeDataForm: {
    stopeId: stope.data.stopeId,
    stopeName: stope.data.stopeName,
    volume: stope.data.volume,
    level: stope.data.level,
    area: stope.data.area,
    pourPresetGroupId: stope.data.pourPresetGroupId,
    pipeArrays: stope.data.pipeArrays,
    startDate: stope.data.startDate,
    endDate: stope.data.endDate,
    ...partial,
  },
});

const computeSharedValue = <T, >(values: T[]): T | typeof MULTIPLE => {
  if (values.length === 0) return MULTIPLE;
  const first = values[0];
  const allSame = values.every((v) => v === first);
  return allSame ? first : MULTIPLE;
};

const localDateToString = (ld: LocalDate | undefined | null): string => {
  if (!ld || !(ld instanceof LocalDate)) return '';
  return ld.toString();
};

interface EditableFieldProps {
  label: string;
  sharedValue: string;
  onCommit: (value: string) => void;
  type?: 'text' | 'number';
  maxLength?: number;
  /** Shown while the field is empty, for a value that falls back to a default. */
  placeholder?: string;
  /** Lets an emptied field commit, to clear a value that falls back to a default. Never clears "(multiple values)". */
  allowEmpty?: boolean;
}

const EditableField = ({
  label,
  sharedValue,
  onCommit,
  type = 'text',
  maxLength,
  placeholder,
  allowEmpty = false,
}: EditableFieldProps) => {
  const [localValue, setLocalValue] = React.useState(sharedValue);
  const [isFocused, setIsFocused] = React.useState(false);
  const originalRef = React.useRef(sharedValue);
  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(sharedValue);
      originalRef.current = sharedValue;
    }
  }, [sharedValue, isFocused]);

  const handleFocus = () => {
    cancelledRef.current = false;
    originalRef.current = localValue;
    setIsFocused(true);
    if (localValue === MULTIPLE) {
      setLocalValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (cancelledRef.current) {
      cancelledRef.current = false;
      setLocalValue(originalRef.current);
      return;
    }
    const canCommitEmpty = allowEmpty && originalRef.current !== MULTIPLE;
    if (localValue !== originalRef.current && localValue !== MULTIPLE && (localValue !== '' || canCommitEmpty)) {
      onCommit(localValue);
    } else {
      setLocalValue(originalRef.current);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      cancelledRef.current = true;
      (e.target as HTMLInputElement).blur();
    }
  };

  const showingMultipleUnfocused = sharedValue === MULTIPLE && !isFocused;
  const displayValue = showingMultipleUnfocused ? MULTIPLE : localValue;
  const displayType = showingMultipleUnfocused ? 'text' : type;

  return (
    <TextField
      label={label}
      size="small"
      fullWidth
      color="secondary"
      type={displayType}
      value={displayValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={showingMultipleUnfocused ? undefined : placeholder}
      InputLabelProps={placeholder && !showingMultipleUnfocused ? { shrink: true } : undefined}
      InputProps={{
        sx: showingMultipleUnfocused ? { color: 'text.disabled', fontStyle: 'italic' } : {},
        inputProps: maxLength ? { maxLength } : undefined,
      }}
    />
  );
};

const sharedDateToDayjs = (value: string): Dayjs | null => (value && value !== MULTIPLE ? LocalDateToDayJs(LocalDate.parse(value)) : null);

// MUI hides an input's placeholder until its label floats, so an empty date field floats it to show "(multiple values)".
export const multipleDatesTextFieldProps = (isMultiple: boolean) => (isMultiple ? {
  placeholder: MULTIPLE,
  InputLabelProps: { shrink: true },
  sx: { '& input::placeholder': { fontStyle: 'italic' } },
} : {});

interface BulkDateFieldProps {
  label: string;
  sharedValue: string;
  onCommit: (date: LocalDate | null) => boolean;
}

// Saves on a calendar pick, a clear, or leaving the field, never per keystroke: parallel saves to the reticulation collide.
const BulkDateField = ({ label, sharedValue, onCommit }: BulkDateFieldProps) => {
  const [draft, setDraft] = React.useState<Dayjs | null>(() => sharedDateToDayjs(sharedValue));
  const touchedRef = React.useRef(false);

  const commit = (date: Dayjs | null) => {
    if (!touchedRef.current || (date && !date.isValid())) return;
    touchedRef.current = false;
    const next = date ? dayJsToLocalDate(date) : null;
    if (localDateToString(next) === sharedValue) return;
    if (!onCommit(next)) setDraft(sharedDateToDayjs(sharedValue));
  };

  React.useEffect(() => {
    setDraft(sharedDateToDayjs(sharedValue));
  }, [sharedValue]);

  return (
    <DatePicker
      format="DD/MM/YYYY"
      label={label}
      value={draft}
      onChange={(date) => {
        touchedRef.current = true;
        setDraft(date);
      }}
      onAccept={commit}
      slotProps={{
        textField: {
          size: 'small',
          fullWidth: true,
          color: 'secondary' as const,
          onBlur: () => commit(draft),
          ...multipleDatesTextFieldProps(sharedValue === MULTIPLE && !draft),
        },
        field: { clearable: true },
      }}
    />
  );
};

type ServiceDateField = 'installedOn' | 'removedOn';

const serviceDatesStayValid = (spools: PCSpool[], field: ServiceDateField, date: LocalDate | null): boolean => spools.every((spool) => {
  const installed = field === 'installedOn' ? date : spool.installedOn;
  const removed = field === 'removedOn' ? date : spool.removedOn;
  return !installed || !removed || !removed.isBefore(installed);
});

const serviceDatePatch = (field: ServiceDateField, date: LocalDate | null): Partial<PCSpool> => (
  field === 'installedOn' ? { installedOn: date ?? undefined } : { removedOn: date ?? undefined }
);

const serviceDateForm = (field: ServiceDateField, date: LocalDate | null): Omit<SpoolUpdateForm, 'spoolId'> => {
  if (field === 'installedOn') return date ? { installedOn: date } : { clearInstalledOn: true };
  return date ? { removedOn: date } : { clearRemovedOn: true };
};

interface EditableSelectProps {
  label: string;
  sharedValue: string;
  options: { value: string; label: string }[];
  onCommit: (value: string) => void;
}

const EditableSelect = ({ label, sharedValue, options, onCommit }: EditableSelectProps) => {
  const displayValue = options.some((o) => o.value === sharedValue) ? sharedValue : '';
  const isMultiple = sharedValue === MULTIPLE;

  const handleChange = (event: SelectChangeEvent<string>) => {
    onCommit(event.target.value);
  };

  return (
    <FormControl size="small" fullWidth color="secondary">
      <InputLabel shrink={isMultiple || !!displayValue}>{label}</InputLabel>
      <Select
        color="secondary"
        value={displayValue}
        label={label}
        onChange={handleChange}
        displayEmpty
        notched={isMultiple || !!displayValue}
        renderValue={(selected) => {
          if (!selected && isMultiple) {
            return <em style={{ color: 'gray' }}>{MULTIPLE}</em>;
          }
          const opt = options.find((o) => o.value === selected);
          return opt?.label ?? selected;
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface EditableCheckboxProps {
  label: string;
  sharedValue: boolean | typeof MULTIPLE;
  onCommit: (value: boolean) => void;
}

const EditableCheckbox = ({ label, sharedValue, onCommit }: EditableCheckboxProps) => {
  const isMultiple = sharedValue === MULTIPLE;
  const checked = sharedValue === true;
  return (
    <FormControlLabel
      control={(
        <Checkbox
          color="secondary"
          checked={checked}
          indeterminate={isMultiple}
          onChange={(e) => onCommit(e.target.checked)}
        />
      )}
      label={label}
    />
  );
};

const getSelectedNodes = (model: Model, selectedIds: string[]): PCNode[] => (
  selectedIds.map((id) => model.nodes[id]).filter(Boolean)
);

const getSelectedStopes = (model: Model, selectedIds: string[]): PCStope[] => {
  const allStopes = Object.values(model.stopes).flat();
  return allStopes.filter((s) => selectedIds.includes(s?.data?.stopeId));
};

const getSelectedSpools = (model: Model, selectedIds: string[]): PCSpool[] => (
  selectedIds.map((id) => model.spools[id]).filter(Boolean)
);

const directionFromSpool = (spool: PCSpool): string => {
  if (spool.allowFlowAB && spool.allowFlowBA) return 'Bidirectional';
  if (spool.allowFlowAB) return 'Forward';
  if (spool.allowFlowBA) return 'Backward';
  return 'Forward';
};

const NodeProperties = ({ model, selectedIds }: { model: Model; selectedIds: string[] }) => {
  const nodes = getSelectedNodes(model, selectedIds);
  const { entityId: projectId, name: projectName } = model.project;
  const bulkUpdateMutation = useBulkUpdateNodesMutation(projectId);
  const roles = useRoles();
  const canEditAdvanced = isAdminUser(roles) || isPcUser(roles);

  const pipeOptions = Object.values(model.pipes).map((p) => ({ value: p.pipeId, label: p.name }));

  const sharedPipeId = computeSharedValue(map((n: PCNode) => n.pipeId, nodes) as string[]);
  const sharedLevel = computeSharedValue(map((n: PCNode) => n.level ?? '', nodes) as string[]);
  const sharedArea = computeSharedValue(map((n: PCNode) => n.area ?? '', nodes) as string[]);
  const sharedRuptureDiskPressure = computeSharedValue(
    map((n: PCNode) => String(n.ruptureDiskPressure ?? 0), nodes) as string[],
  );
  const sharedChokeHead = computeSharedValue(
    map((n: PCNode) => String(n.chokeHead ?? 0), nodes) as string[],
  );
  const sharedPit = computeSharedValue(map((n: PCNode) => n.pit ?? '', nodes) as string[]);
  const sharedBoosterPressure = computeSharedValue(
    map((n: PCNode) => String(n.boosterPressure ?? 0), nodes) as string[],
  );
  const sharedIsPlantNode = computeSharedValue(map((n: PCNode) => !!n.isPlantNode, nodes) as boolean[]);

  const commitNodeUpdates = (updater: (node: PCNode) => Partial<PCNode>) => {
    const updateForms: UpdateModelNodeDataForm[] = nodes.map((node) => {
      const updates = updater(node);
      const updated = { ...node, ...updates };
      Object.assign(node, updates);
      return buildNodeUpdateForm(updated);
    });
    bulkUpdateMutation.mutate({ projectId, projectName: projectName ?? null, data: updateForms });
  };

  return (
    <>
      <EditableSelect
        label={i18n._('Pipe Type')}
        sharedValue={sharedPipeId as string}
        options={pipeOptions}
        onCommit={(val) => commitNodeUpdates(() => ({ pipeId: val }))}
      />
      <EditableField
        label={i18n._('Level')}
        sharedValue={sharedLevel as string}
        onCommit={(val) => commitNodeUpdates(() => ({ level: val || undefined }))}
      />
      <EditableField
        label={i18n._('Area')}
        sharedValue={sharedArea as string}
        onCommit={(val) => commitNodeUpdates(() => ({ area: val || undefined }))}
      />
      <EditableField
        label={i18n._('Rupture Disk Pressure')}
        sharedValue={sharedRuptureDiskPressure as string}
        type="number"
        onCommit={(val) => commitNodeUpdates(() => ({ ruptureDiskPressure: parseFloat(val) || 0 }))}
      />
      <EditableField
        label={i18n._('Choke Station Head')}
        sharedValue={sharedChokeHead as string}
        type="number"
        onCommit={(val) => commitNodeUpdates(() => ({ chokeHead: parseFloat(val) || 0 }))}
      />
      <EditableField
        label={i18n._('Pressure Transmitter Name')}
        sharedValue={sharedPit as string}
        onCommit={(val) => commitNodeUpdates(() => ({ pit: val || undefined }))}
      />
      {canEditAdvanced && (
        <>
          <EditableField
            label={i18n._('Booster Pump Pressure')}
            sharedValue={sharedBoosterPressure as string}
            type="number"
            onCommit={(val) => commitNodeUpdates(() => ({ boosterPressure: parseFloat(val) || 0 }))}
          />
          <EditableCheckbox
            label={i18n._('Is Backfill Plant')}
            sharedValue={sharedIsPlantNode as boolean | typeof MULTIPLE}
            onCommit={(val) => commitNodeUpdates(() => ({ isPlantNode: val }))}
          />
        </>
      )}
    </>
  );
};

const StopeProperties = ({ model, selectedIds }: { model: Model; selectedIds: string[] }) => {
  const stopes = getSelectedStopes(model, selectedIds);
  const { entityId: projectId, name: projectName } = model.project;
  const updateStopeMutation = useUpdateStopeMutation(projectId);
  const { data: pourPresetGroupsData } = useGetPourPresetGroupsQuery({ projectId, page: 1, pageSize: 1000 });

  const pipeOptions = Object.values(model.pipes).map((p) => ({ value: p.pipeId, label: p.name }));
  const pourPresetGroupOptions = (pourPresetGroupsData?.items ?? []).map((g) => ({
    value: g.entityId ?? '', label: g.name ?? '',
  }));

  const sharedLevel = computeSharedValue(stopes.map((s) => s.data?.level ?? ''));
  const sharedArea = computeSharedValue(stopes.map((s) => s.data?.area ?? ''));
  const sharedVolume = computeSharedValue(stopes.map((s) => String(s.data?.volume ?? 0)));
  const sharedPourPresetGroupId = computeSharedValue(stopes.map((s) => s.data?.pourPresetGroupId ?? ''));

  const pipeArrayValues = stopes.map((s) => {
    const pa = s.data?.pipeArrays?.[0];
    return pa?.pipeId?.value ?? '';
  });
  const sharedPipeId = computeSharedValue(pipeArrayValues);

  const hLengths = stopes.map((s) => String(s.data?.pipeArrays?.[0]?.pipeHorizontalLength ?? 0));
  const sharedHorizontalLength = computeSharedValue(hLengths);

  const vLengths = stopes.map((s) => String(s.data?.pipeArrays?.[0]?.pipeVerticalLength ?? 0));
  const sharedVerticalLength = computeSharedValue(vLengths);

  const startDates = stopes.map((s) => localDateToString(s.data?.startDate));
  const sharedStartDate = computeSharedValue(startDates);

  const endDates = stopes.map((s) => localDateToString(s.data?.endDate));
  const sharedEndDate = computeSharedValue(endDates);

  const commitStopeUpdates = (updater: (stope: PCStope) => Partial<UpdateStopeDataForm['stopeDataForm']>) => {
    const updateForms = stopes.map((stope) => buildStopeUpdateForm(stope, updater(stope)));
    updateStopeMutation.mutate({ projectName: projectName ?? '', data: updateForms });
  };

  const startDateDayjs: Dayjs | null = sharedStartDate !== MULTIPLE && sharedStartDate
    ? LocalDateToDayJs(LocalDate.parse(sharedStartDate))
    : null;

  const endDateDayjs: Dayjs | null = sharedEndDate !== MULTIPLE && sharedEndDate
    ? LocalDateToDayJs(LocalDate.parse(sharedEndDate))
    : null;

  return (
    <>
      <EditableField
        label={i18n._('Level')}
        sharedValue={sharedLevel as string}
        onCommit={(val) => commitStopeUpdates(() => ({ level: val || undefined }))}
      />
      <EditableField
        label={i18n._('Area')}
        sharedValue={sharedArea as string}
        onCommit={(val) => commitStopeUpdates(() => ({ area: val || undefined }))}
      />
      <EditableField
        label={i18n._('Volume')}
        sharedValue={sharedVolume as string}
        type="number"
        onCommit={(val) => commitStopeUpdates(() => ({ volume: parseFloat(val) || 0 }))}
      />
      <EditableSelect
        label={i18n._('Pipe Type')}
        sharedValue={sharedPipeId as string}
        options={pipeOptions}
        onCommit={(val) => commitStopeUpdates(() => ({
          pipeArrays: [{ pipeId: { value: val }, pipeHorizontalLength: 0, pipeVerticalLength: 0 }],
        }))}
      />
      <EditableField
        label={i18n._('Horizontal Length')}
        sharedValue={sharedHorizontalLength as string}
        type="number"
        onCommit={(val) => commitStopeUpdates((stope) => ({
          pipeArrays: [{
            pipeId: stope.data?.pipeArrays?.[0]?.pipeId,
            pipeHorizontalLength: parseFloat(val) || 0,
            pipeVerticalLength: stope.data?.pipeArrays?.[0]?.pipeVerticalLength ?? 0,
          }],
        }))}
      />
      <EditableField
        label={i18n._('Vertical Length')}
        sharedValue={sharedVerticalLength as string}
        type="number"
        onCommit={(val) => commitStopeUpdates((stope) => ({
          pipeArrays: [{
            pipeId: stope.data?.pipeArrays?.[0]?.pipeId,
            pipeHorizontalLength: stope.data?.pipeArrays?.[0]?.pipeHorizontalLength ?? 0,
            pipeVerticalLength: parseFloat(val) || 0,
          }],
        }))}
      />
      <EditableSelect
        label={i18n._('Pour Preset Group')}
        sharedValue={sharedPourPresetGroupId as string}
        options={pourPresetGroupOptions}
        onCommit={(val) => commitStopeUpdates(() => ({ pourPresetGroupId: val || undefined }))}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <DatePicker
          format="DD/MM/YYYY"
          label={i18n._('Start Date')}
          value={startDateDayjs}
          onChange={(date) => {
            const ld = date ? dayJsToLocalDate(date) : undefined;
            commitStopeUpdates(() => ({ startDate: ld }));
          }}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              color: 'secondary' as const,
              ...multipleDatesTextFieldProps(sharedStartDate === MULTIPLE),
            },
          }}
        />
        <DatePicker
          format="DD/MM/YYYY"
          label={i18n._('End Date')}
          value={endDateDayjs}
          onChange={(date) => {
            const ld = date ? dayJsToLocalDate(date) : undefined;
            commitStopeUpdates(() => ({ endDate: ld }));
          }}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              color: 'secondary' as const,
              ...multipleDatesTextFieldProps(sharedEndDate === MULTIPLE),
            },
          }}
        />
      </LocalizationProvider>
    </>
  );
};

// Pipe, flow direction and flow-loop length are all properties of the edge, so every commit here is a
// patch of the selected spools - one request for the whole selection, never one per spool (the
// reticulation document is one per mine and parallel saves collide).
const SpoolProperties = ({ model, selectedIds }: { model: Model; selectedIds: string[] }) => {
  const spools = getSelectedSpools(model, selectedIds);
  const { entityId: projectId } = model.project;
  const bulkUpdateSpoolsMutation = useBulkUpdateSpoolsMutation(projectId);
  const { setLegendData, setShowLegend } = useSceneLegend();

  const pipeOptions = Object.values(model.pipes).map((p) => ({ value: p.pipeId, label: p.name }));
  const directionOptions = [
    { value: 'Forward', label: i18n._('Forward') },
    { value: 'Backward', label: i18n._('Backward') },
    { value: 'Bidirectional', label: i18n._('Bidirectional') },
  ];

  const statusOptions = [
    { value: String(SpoolStatus.Active), label: i18n._('Active') },
    { value: String(SpoolStatus.Inactive), label: i18n._('Inactive') },
  ];

  const sharedName = computeSharedValue(spools.map((s) => s.name ?? ''));
  const sharedLevel = computeSharedValue(spools.map((s) => s.level ?? ''));
  const sharedArea = computeSharedValue(spools.map((s) => s.area ?? ''));
  const sharedDirection = computeSharedValue(spools.map(directionFromSpool));
  const sharedPipeType = computeSharedValue(spools.map((s) => s.pipe?.pipeId ?? ''));
  const sharedChokeLength = computeSharedValue(spools.map((s) => String(s.chokeLength ?? 0)));
  const sharedStatus = computeSharedValue(spools.map((s) => String(s.status ?? SpoolStatus.Active)));
  const sharedInstalledOn = computeSharedValue(spools.map((s) => localDateToString(s.installedOn)));
  const sharedRemovedOn = computeSharedValue(spools.map((s) => localDateToString(s.removedOn)));
  const sharedOther = computeSharedValue(spools.map((s) => s.other ?? ''));

  const showPipeTypeVisuals = React.useCallback(() => {
    model.setSpoolColoursByPipe(true);
    setLegendData(generatePipeTypeLegend(model, model.recipeState));
    setShowLegend(true);
  }, [model, setLegendData, setShowLegend]);

  const commitSpoolUpdates = (updater: (spool: PCSpool) => Partial<PCSpool>, toForm: (spool: PCSpool) => SpoolUpdateForm) => {
    const forms = spools.map((spool) => {
      model.setSpoolProperties(spool.spoolId, updater(spool));
      return toForm(spool);
    });
    if (forms.length === 0) return;
    bulkUpdateSpoolsMutation.mutate({ projectId, spools: forms });
  };

  const commitDirectionUpdate = (direction: string) => {
    const allowFlowAB = direction === 'Forward' || direction === 'Bidirectional';
    const allowFlowBA = direction === 'Backward' || direction === 'Bidirectional';
    commitSpoolUpdates(
      () => ({ allowFlowAB, allowFlowBA }),
      (spool) => ({ spoolId: spool.spoolId, allowFlowAB, allowFlowBA }),
    );
  };

  const commitPipeTypeUpdate = (pipeId: string) => {
    const newPipe = Object.values(model.pipes).find((p) => p.pipeId === pipeId);
    if (!newPipe) return;
    showPipeTypeVisuals();
    commitSpoolUpdates(
      () => ({ pipe: newPipe }),
      (spool) => ({ spoolId: spool.spoolId, pipeId: newPipe.pipeId }),
    );
  };

  const commitChokeLengthUpdate = (value: string) => {
    const chokeLength = parseFloat(value) || 0;
    commitSpoolUpdates(
      () => ({ chokeLength }),
      (spool) => ({ spoolId: spool.spoolId, chokeLength }),
    );
  };

  // An empty value clears the field (the server reads '' as clear); a cleared name falls back to the end nodes.
  const commitTextUpdate = (field: SpoolTextField) => (value: string) => {
    const text = value.trim();
    commitSpoolUpdates(
      () => textPatch(field, text || undefined),
      (spool) => ({ spoolId: spool.spoolId, ...textPatch(field, text) }),
    );
  };

  const commitStatusUpdate = (value: string) => {
    const status = Number(value) as SpoolStatus;
    commitSpoolUpdates(
      () => ({ status }),
      (spool) => ({ spoolId: spool.spoolId, status }),
    );
  };

  // The server rejects the whole patch if any spool would end up removed before it was installed, so check first.
  const commitServiceDateUpdate = (field: ServiceDateField) => (date: LocalDate | null): boolean => {
    if (!serviceDatesStayValid(spools, field, date)) {
      enqueueSnackbar(i18n._('Removed date must be on or after the installed date'), { variant: 'error' });
      return false;
    }
    commitSpoolUpdates(
      () => serviceDatePatch(field, date),
      (spool) => ({ spoolId: spool.spoolId, ...serviceDateForm(field, date) }),
    );
    return true;
  };

  return (
    <>
      <EditableField
        label={i18n._('Spool Name')}
        sharedValue={sharedName as string}
        maxLength={SPOOL_NAME_MAX_LENGTH}
        placeholder={spools.length === 1 ? spoolEndsLabel(spools[0]) : undefined}
        allowEmpty
        onCommit={commitTextUpdate('name')}
      />
      <EditableField
        label={i18n._('Level')}
        sharedValue={sharedLevel as string}
        maxLength={SPOOL_TAG_MAX_LENGTH}
        allowEmpty
        onCommit={commitTextUpdate('level')}
      />
      <EditableField
        label={i18n._('Area')}
        sharedValue={sharedArea as string}
        maxLength={SPOOL_TAG_MAX_LENGTH}
        allowEmpty
        onCommit={commitTextUpdate('area')}
      />
      <EditableSelect
        label={i18n._('Direction')}
        sharedValue={sharedDirection as string}
        options={directionOptions}
        onCommit={commitDirectionUpdate}
      />
      <EditableSelect
        label={i18n._('Pipe Type')}
        sharedValue={sharedPipeType as string}
        options={pipeOptions}
        onCommit={commitPipeTypeUpdate}
      />
      <EditableField
        label="Flow Loop Length"
        sharedValue={sharedChokeLength as string}
        type="number"
        onCommit={commitChokeLengthUpdate}
      />
      <EditableSelect
        label={i18n._('Status')}
        sharedValue={sharedStatus as string}
        options={statusOptions}
        onCommit={commitStatusUpdate}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <BulkDateField
          label={i18n._('Installed')}
          sharedValue={sharedInstalledOn as string}
          onCommit={commitServiceDateUpdate('installedOn')}
        />
        <BulkDateField
          label={i18n._('Removed')}
          sharedValue={sharedRemovedOn as string}
          onCommit={commitServiceDateUpdate('removedOn')}
        />
      </LocalizationProvider>
      <EditableField
        label={i18n._('Other')}
        sharedValue={sharedOther as string}
        maxLength={SPOOL_OTHER_MAX_LENGTH}
        allowEmpty
        onCommit={commitTextUpdate('other')}
      />
    </>
  );
};

type SharedTag = 'level' | 'area';

interface SharedTagPropertiesProps {
  model: Model;
  nodeIds: string[];
  spoolIds: string[];
  stopeIds: string[];
}

// Level and area are what nodes, spools and stopes share. All three live in the one reticulation document, so the
// saves run one after another and the model refreshes once, after the last.
const SharedTagProperties = ({
  model,
  nodeIds,
  spoolIds,
  stopeIds,
}: SharedTagPropertiesProps) => {
  const nodes = getSelectedNodes(model, nodeIds);
  const spools = getSelectedSpools(model, spoolIds);
  const stopes = getSelectedStopes(model, stopeIds);
  const { entityId: projectId, name: projectName } = model.project;
  const queryClient = useQueryClient();
  const updateNodesMutation = useBulkUpdateNodesSilentMutation(projectId);
  const updateSpoolsMutation = useBulkUpdateSpoolsSilentMutation(projectId);
  const updateStopesMutation = useUpdateStopeSilentMutation(projectId);
  const maxLength = spools.length > 0 ? SPOOL_TAG_MAX_LENGTH : undefined;

  const sharedTag = (tag: SharedTag): string => computeSharedValue([
    ...nodes.map((node) => node[tag] ?? ''),
    ...spools.map((spool) => spool[tag] ?? ''),
    ...stopes.map((stope) => stope.data?.[tag] ?? ''),
  ]);

  // An empty value clears the tag: nodes and stopes save it unset, and the spool patch sends '' (clear).
  const commitTag = (tag: SharedTag) => async (value: string) => {
    const text = value.trim();
    const patch = textPatch(tag, text || undefined);
    nodes.forEach((node) => model.setNodeProperties(node.id, patch));
    spools.forEach((spool) => model.setSpoolProperties(spool.spoolId, patch));

    const saves: (() => Promise<unknown>)[] = [
      ...(nodes.length > 0
        ? [() => updateNodesMutation.mutateAsync({ projectId, projectName: projectName ?? null, data: nodes.map((node) => buildNodeUpdateForm({ ...node, ...patch })) })]
        : []),
      ...(spools.length > 0
        ? [() => updateSpoolsMutation.mutateAsync({ projectId, spools: spools.map((spool) => ({ spoolId: spool.spoolId, ...textPatch(tag, text) })) })]
        : []),
      ...(stopes.length > 0
        ? [() => updateStopesMutation.mutateAsync({ projectName: projectName ?? '', data: stopes.map((stope) => buildStopeUpdateForm(stope, patch)) })]
        : []),
    ];
    const saved = await saves
      .reduce((chain, save) => chain.then(save), Promise.resolve() as Promise<unknown>)
      .then(() => true, () => false);
    invalidateBulkNodeKeys(queryClient);
    refreshMineModel(queryClient);
    if (saved) enqueueSnackbar(i18n._('Reticulation data updated'), { variant: 'success' });
  };

  return (
    <>
      <EditableField
        label={i18n._('Level')}
        sharedValue={sharedTag('level')}
        maxLength={maxLength}
        allowEmpty
        onCommit={commitTag('level')}
      />
      <EditableField
        label={i18n._('Area')}
        sharedValue={sharedTag('area')}
        maxLength={maxLength}
        allowEmpty
        onCommit={commitTag('area')}
      />
    </>
  );
};

const BulkEditPropertiesPanel = ({ model }: BulkEditPropertiesPanelProps) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const sceneState = getBulkEditSceneState();
  const { entityType, selectedIds } = sceneState;

  React.useEffect(() => {
    const unsubscribe = subscribeToBulkEditSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  if (entityType === 'Node') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle2">{i18n._('Properties')}</Typography>
        <NodeProperties model={model} selectedIds={selectedIds} />
      </Box>
    );
  }
  if (entityType === 'Stope') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle2">{i18n._('Properties')}</Typography>
        <StopeProperties model={model} selectedIds={selectedIds} />
      </Box>
    );
  }
  if (entityType === 'Spool') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle2">{i18n._('Properties')}</Typography>
        <SpoolProperties model={model} selectedIds={selectedIds} />
      </Box>
    );
  }

  const { nodeIds, spoolIds, stopeIds } = splitSelectedIdsByType(model, selectedIds);
  const presentTypes = [nodeIds.length > 0, spoolIds.length > 0, stopeIds.length > 0].filter(Boolean).length;

  if (presentTypes === 0) return null;
  if (presentTypes > 1) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle2">{i18n._('Properties')}</Typography>
        <Typography variant="body2" color="text.secondary">
          Mixed selection
        </Typography>
        <SharedTagProperties model={model} nodeIds={nodeIds} spoolIds={spoolIds} stopeIds={stopeIds} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle2">{i18n._('Properties')}</Typography>
      {nodeIds.length > 0 && <NodeProperties model={model} selectedIds={nodeIds} />}
      {stopeIds.length > 0 && <StopeProperties model={model} selectedIds={stopeIds} />}
      {spoolIds.length > 0 && <SpoolProperties model={model} selectedIds={spoolIds} />}
    </Box>
  );
};

export default BulkEditPropertiesPanel;
